import { Button, Card, IconButton, Input, Typography, Dialog, Menu } from "@material-tailwind/react";
import { HiOutlinePlus, HiPencil, HiSearch, HiX, HiOutlineChevronLeft, HiOutlineChevronRight, HiOutlineDotsHorizontal, HiTrash, HiOutlineExclamationCircle } from "react-icons/hi";
import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import ManufacturerAPI from '../api/manufacturer';
import PaginationCustom from '../components/molecules/PaginationCustom';
import { toast } from 'react-toastify';
import debounce from 'lodash/debounce';
import { useDialog } from '../context/DialogContext';

const SkeletonRow = () => (
  <div className="w-full h-12 rounded bg-primary/5 animate-pulse"></div>
);

const Manufacturer = () => {
  const [manufacturers, setManufacturers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    totalItems: 0,
    totalPages: 0
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [manufacturerToDelete, setManufacturerToDelete] = useState(null);

  // Get dialog functions from context
  const { openManufacturerDialog } = useDialog();

  const currentPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const currentLimit = Math.max(10, parseInt(searchParams.get('limit') || '50', 10) || 50);

  const debouncedSearch = useCallback(
    debounce((keyword, page, limit) => {
      if (keyword) {
        searchManufacturers(keyword, page, limit);
      } else {
        fetchManufacturers(page, limit);
      }
    }, 500),
    []
  );

  useEffect(() => {
    const page = parseInt(searchParams.get('page') || '1', 10);
    const search = searchParams.get('search') || '';
    const limit = currentLimit;
    setSearchKeyword(search);
    debouncedSearch(search, page, limit);
  }, [searchParams]);

  const handleSearch = (e) => {
    const keyword = e.target.value;
    setSearchKeyword(keyword);
    setSearchParams({ page: '1', search: keyword, limit: currentLimit.toString() });
  };

  const handlePageChange = (newPage) => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      params.set('page', newPage.toString());
      params.set('limit', currentLimit.toString());
      return params;
    });
  };

  const handleSizeChange = (newSize) => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      params.set('page', '1');
      params.set('limit', newSize.toString());
      return params;
    });
  };

  const searchManufacturers = async (keyword, page, limit) => {
    try {
      setLoading(true);
      const response = await ManufacturerAPI.search(keyword, page, limit);
      setManufacturers(response.results);
      setPagination({
        page: response.pagination?.page || 1,
        totalItems: response.pagination?.totalItems || 0,
        totalPages: response.pagination?.totalPages || 0
      });
    } catch (error) {
      toast.error('Failed to search manufacturers');
    } finally {
      setLoading(false);
    }
  };

  const fetchManufacturers = async (page, limit) => {
    try {
      setLoading(true);
      const response = await ManufacturerAPI.getByPage(page, limit);
      setManufacturers(response.results);
      setPagination({
        page: response.pagination?.page || 1,
        totalItems: response.pagination?.totalItems || 0,
        totalPages: response.pagination?.totalPages || 0
      });
    } catch (error) {
      toast.error('Failed to fetch manufacturers');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (manufacturer) => {
    setManufacturerToDelete(manufacturer);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      const response = await ManufacturerAPI.delete(manufacturerToDelete._id);
      if (response) {
        toast.success('Manufacturer deleted successfully');
        setDeleteDialogOpen(false);
        setManufacturerToDelete(null);

        if (searchKeyword) {
          searchManufacturers(searchKeyword, currentPage, currentLimit);
        } else {
          fetchManufacturers(currentPage, currentLimit);
        }
      }
    } catch (error) {
      toast.error('Failed to delete manufacturer');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (manufacturerUri) => {
    openManufacturerDialog({
      mode: 'edit',
      manufacturerUri: manufacturerUri,
      onSuccess: (updatedManufacturer) => {
        if (searchKeyword) {
          searchManufacturers(searchKeyword, currentPage, currentLimit);
        } else {
          fetchManufacturers(currentPage, currentLimit);
        }
        toast.success(`Manufacturer ${updatedManufacturer.name} updated successfully`);
      }
    });
  };

  const handleCreateClick = () => {
    openManufacturerDialog({
      mode: 'create',
      onSuccess: (newManufacturer) => {
        fetchManufacturers(currentPage, currentLimit); // Refresh the list after creation
        toast.success(`Manufacturer ${newManufacturer.name} created successfully`);
      }
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="w-full max-w-[300px]">
          <Input
            className="bg-white"
            placeholder="Search by name"
            disabled={loading}
            value={searchKeyword}
            onChange={handleSearch}
          >
            <Input.Icon>
              <HiSearch className="size-full" />
            </Input.Icon>
          </Input>
        </div>
        <Button
          className="flex items-center gap-2 px-4"
          disabled={loading}
          onClick={handleCreateClick}
        >
          <HiOutlinePlus className="w-4 h-4" /> New Manufacturer
        </Button>
      </div>

      {!loading && pagination.totalItems > 0 && (
        <PaginationCustom
          currentPage={currentPage}
          pageSize={currentLimit}
          totalItems={pagination.totalItems}
          onPageChange={handlePageChange}
          onSizeChange={handleSizeChange}
          loading={loading}
        />
      )}

      <div className="grid grid-cols-1 gap-2 mb-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
        {loading ? (
          Array.from({ length: 20 }).map((_, index) => (
            <SkeletonRow key={index} />
          ))
        ) : manufacturers.map((manufacturer) => (
          <div
            key={manufacturer._id}
            onClick={() => handleEditClick(manufacturer.uri)}
            className="relative h-full px-4 py-3 leading-none transition bg-white border rounded-md shadow-none cursor-pointer text-primary hover:border-primary/50 group"
          >
            <span className="break-words text-14">{manufacturer.name}</span>
            <div className="absolute hidden -top-2 -right-2 group-hover:block">
              <IconButton
                size="sm"
                className="min-w-0 min-h-0 duration-150 border rounded-full size-5 border-primary hover:bg-red-500 hover:text-white hover:border-red-500"
                color="secondary"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDelete(manufacturer);
                }}
              >
                <HiX className="size-3" />
              </IconButton>
            </div>
          </div>
        ))}
        {!loading && manufacturers.length === 0 && (
          <div className="flex flex-col items-center justify-center w-full h-full gap-5 col-span-full">
            <HiOutlineExclamationCircle className="opacity-50 size-12 text-blue-gray-400" />
            <Typography className="font-normal text-blue-gray-400">
              {searchKeyword ? 'No manufacturers found matching your search' : 'No manufacturers found'}
            </Typography>
            {!searchKeyword && (
              <Button
                className="flex items-center gap-2 px-4"
                onClick={handleCreateClick}
              >
                <HiOutlinePlus className="w-4 h-4" /> Create Manufacturer
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        handler={() => setDeleteDialogOpen(false)}
        size="sm"
      >
        <Dialog.Overlay>
          <Dialog.Content>
            <div className="p-0">
              <div className="p-4 text-center">
                <Typography variant="h5" className="mb-4">
                  Delete Manufacturer
                </Typography>
                <Typography>
                  Are you sure you want to delete "{manufacturerToDelete?.name}"?<br />
                  This action cannot be undone.
                </Typography>
              </div>
              <div className="flex justify-center gap-4 p-4 border-t">
                <Button
                  onClick={() => setDeleteDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  color="red"
                  onClick={confirmDelete}
                  disabled={loading}
                >
                  {loading ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog>
    </div>
  );
};

export default Manufacturer; 